import { createContext, useEffect, useReducer, useMemo } from "react";
import { useRetailerHook, useLanguageHook } from "../Hooks/use-context-hooks";
import { mapProductsByCategory } from "../Utils/category-map";
import { useLocation } from "react-router-dom";

export const ProductContext = createContext({
  publicProducts: [],
  allProducts: [],
  filteredProducts:[],
  setPublicProducts: () => { },
  fetchProducts: () => { },
  isLoading: false,
  error: null,

  productsByCategory: {},
  filteredProductsByCategory: {},
});

export const PRODUCT_ACTION_TYPES = {
  SET_PRODUCTS: 'SET_PRODUCTS',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
};

const productReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case PRODUCT_ACTION_TYPES.SET_PRODUCTS:
      return {
        ...state,
        publicProducts: payload.filteredProducts,
        allProducts: payload.allProducts,
        isLoading: false,
        error: null,

        productsByCategory: payload.productsByCategory,
        filteredProductsByCategory: payload.filteredProductsByCategory,
      };
    case PRODUCT_ACTION_TYPES.SET_LOADING:
      return { ...state, isLoading: true, error: null };
    case PRODUCT_ACTION_TYPES.SET_ERROR:
      return { ...state, isLoading: false, error: payload };
    default:
      throw new Error(`Unhandled type ${type} in productReducer`);
  }
};

const INITIAL_PUBLIC_PRODUCT_STATE = {
  publicProducts: [],
  allProducts: [],
  filteredProducts: [],
  isLoading: false,
  error: null,

  productsByCategory: {},
  filteredProductsByCategory: {},
};

export const ProductProvider = ({ children }) => {
  const [state, dispatch] = useReducer(productReducer, INITIAL_PUBLIC_PRODUCT_STATE);
  const { isHomeDepotApp } = useRetailerHook();
  const { language } = useLanguageHook();


  // const { publicProducts, allProducts, isLoading, error } = state;
  const { publicProducts, allProducts, isLoading, error, productsByCategory, filteredProducts, filteredProductsByCategory } = state;

  const translateProducts = (products, language) => {
   
    const langKey = language.toUpperCase();



return products.map(product => ({
      // ...product,
      _id: product._id,
      availability: product[`availability${langKey}`] || product.availability,
      category: product[`category${langKey}`],
      categoryEN: product.categoryEN || product.category,
      categoryFR: product.categoryFR,
      colour: product[`colour${langKey}`],
      interactiveImageEN: product.interactiveImageEN,
       interactiveImageFR: product.interactiveImageFR,
      image: product.image,
      installGuideLink: product[`installGuideLink${langKey}`],
      logos: product.logos,
      msrp: product.msrp,
      ownerManualLink: product[`ownerManualLink${langKey}`],
      qrCodeSpecSheet: product[`qrCodeSpecSheet${langKey}`],
      sections: product.sections || [],
      subtitle: product.subtitle,
      specList1: product[`specList1${langKey}`],
      specList2: product[`specList2${langKey}`],
      specList3: product[`specList3${langKey}`],
      specList4: product[`specList4${langKey}`],
      specSheetLink: product[`specSheetLink${langKey}`],
      specTitle1: product[`specTitle1${langKey}`],
      specTitle2: product[`specTitle2${langKey}`],
      specTitle3: product[`specTitle3${langKey}`],
      specTitle4: product[`specTitle4${langKey}`],
      store: product.store,
      subcategory: product[`subcategory${langKey}`],
      subcategoryEN: product.subcategoryEN || product.subcategory,
      subcategoryFR: product.subcategoryFR,
      subtitle: product[`subtitle${langKey}`],
      title: product.title,
      upcCode: product[`upcCode${langKey}`],
      videos: product[`videos${langKey}`],
    }));
  }; 

const setProducts = (products, lang) => {
    const filteredProducts = isHomeDepotApp.isHomeDepotActive
      ? products
      : products.filter(product => product.store === 'LG');
    // : products.filter(product => product.store !== 'hd');

    const translatedFilteredProducts = translateProducts(filteredProducts, lang);
    const translatedAllProducts = translateProducts(products, lang);

    // Use your existing mapProductsByCategory utility
    const productsByCategory = mapProductsByCategory(translatedAllProducts);
    const filteredProductsByCategory = mapProductsByCategory(translatedFilteredProducts);

    dispatch({
      type: PRODUCT_ACTION_TYPES.SET_PRODUCTS,
      payload: {
        filteredProducts: translatedFilteredProducts,
        allProducts: translatedAllProducts,
        productsByCategory,
        filteredProductsByCategory,

      }
    });
  };

  const setLoading = () => dispatch({ type: PRODUCT_ACTION_TYPES.SET_LOADING });
  const setError = (errMsg) => dispatch({ type: PRODUCT_ACTION_TYPES.SET_ERROR, payload: errMsg });

  // FIXED HERE: fetchProducts now properly accepts the language parameter
  const fetchProducts = async (lang) => {
    setLoading();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/products`);
        //  const response = await fetch(`http://localhost:3000/products`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setProducts(data.allProducts, lang); // forward language here properly
    } catch (err) {
      setError(`Error: ${err.message}`);
    }
  };

  useEffect(() => {
    fetchProducts(language); // now correctly passes language on effect run
  }, [isHomeDepotApp.isHomeDepotActive, language]);
  // console.log('public products', publicProducts)

  const value = useMemo(() => ({
    publicProducts,
    allProducts,
    setPublicProducts: (products) => setProducts(products, language),
    fetchProducts: () => fetchProducts(language),
    isLoading,
    error,
    filteredProducts,
    productsByCategory,
    filteredProductsByCategory,

    // }), [publicProducts, allProducts, isLoading, error, language]);
  }), [publicProducts, allProducts, productsByCategory, filteredProducts, filteredProductsByCategory, isLoading, error, language]);

  //  console.log('products by cat',productsByCategory,filteredProductsByCategory)
  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
};
